import {
  Box,
  Button,
  ColumnLayout,
  Container,
  FileUpload,
  FormField,
  Header,
  Select,
  SpaceBetween,
  StatusIndicator,
  Textarea,
  Toggle
} from '@amzn/awsui-components-core';
import Input from '@amzn/awsui-components-core/input';
import { FormikProps } from 'formik';
import React, { useEffect, useRef, useState } from 'react';
import { useAppContext } from 'src/components/context/AppContextProvider';
import {
  labelInputToLabelDropdownOptions,
  labelInputToSelectDropdown,
  selectDropdownToLabel
} from 'src/components/generic-components/LabelDropdownUtility';
import { characterCountConstraintMessage } from 'src/utilities/CommonUtilities';
import { ApplicationWizard } from '../../AdminModels';
import { APP_NAME_MAX_LENGTH, FULL_DESCRIPTION_MAX_LENGTH, SHORT_DESCRIPTION_MAX_LENGTH } from './StepsValidations';
import { BROADCAST_URL_EXAMPLE } from 'src/components/generic-components/broadcast-video';
import { Titles } from 'src/components/das-finsuite/das-finsuite-constants';
import { LogoType } from 'src/components/context/AppContextModels';
import { BUNDLED_LOGO_OPTIONS } from './LogoLibraryConstants';
import EmbeddedNavPreview from './EmbeddedNavPreview';
import { AppLogoService } from 'src/services/AppLogoService';

interface ApplicationDetailsProps {
  formik: FormikProps<ApplicationWizard>;
}

const ApplicationDetails: React.FC<ApplicationDetailsProps> = ({ formik }) => {
  const appContext = useAppContext();
  const sessionIdRef = useRef(crypto.randomUUID());
  const originalLogoConfigRef = useRef(formik.values.applicationDetails?.logoConfig ?? null);
  const [logoFile, setLogoFile] = useState<File[]>([]);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  // Load existing S3 logo for preview when editing an app
  const s3LocationKey = formik.values.applicationDetails?.logoConfig?.s3Location?.key;
  useEffect(() => {
    const s3Location = formik.values.applicationDetails?.logoConfig?.s3Location;
    if (!s3Location) return;

    let cancelled = false;
    AppLogoService.getLogoPreviewUrl(s3Location).then((url) => {
      if (!cancelled && url) setPreviewUrl(url);
    });
    return () => {
      cancelled = true;
    };
  }, [s3LocationKey]);

  const isAppSpecific = formik.values.applicationDetails?.logoConfig?.logoType === 'app-specific';

  return (
    <>
      {/* Application Overview  */}
      <Box margin={{ bottom: 'l' }}>
        <Container header={<Header variant="h2">Application Overview</Header>}>
          <SpaceBetween size="m" direction="vertical">
            <ColumnLayout columns={3}>
              <FormField
                label="Application Name"
                description="Enter the unique application name"
                errorText={formik.touched.applicationDetails?.applicationName && formik.errors.applicationDetails?.applicationName}
                constraintText={characterCountConstraintMessage(APP_NAME_MAX_LENGTH, formik.values.applicationDetails?.applicationName?.length || 0)}
                stretch={true}
              >
                <Input
                  placeholder="Enter application name"
                  onChange={({ detail }) => {
                    formik.setFieldValue('applicationDetails.applicationName', detail.value);
                  }}
                  onBlur={() => {
                    formik.setFieldTouched('applicationDetails.applicationName', true);
                  }}
                  value={formik.values.applicationDetails?.applicationName || ''}
                />
              </FormField>

              <FormField label="Embedding Enabled" description="Allow this app to be embedded within FinSuite">
                <Toggle
                  checked={formik.values.applicationDetails?.embeddingEnabled || false}
                  onChange={({ detail }) => {
                    formik.setFieldValue('applicationDetails.embeddingEnabled', detail.checked);
                  }}
                >
                  {formik.values.applicationDetails?.embeddingEnabled ? 'Enabled' : 'Disabled'}
                </Toggle>
              </FormField>

              <FormField label="Flagship Product" description="Mark this app as a flagship product">
                <Toggle
                  checked={formik.values.applicationDetails?.isFlagship || false}
                  onChange={({ detail }) => {
                    formik.setFieldValue('applicationDetails.isFlagship', detail.checked);
                  }}
                >
                  {formik.values.applicationDetails?.isFlagship ? 'Enabled' : 'Disabled'}
                </Toggle>
              </FormField>
            </ColumnLayout>

            <ColumnLayout columns={3}>
              <FormField
                label="FinTech Team"
                description="Select the DaS FinTech team who owns this app"
                errorText={
                  formik.touched.applicationDetails?.applicationOwnerLabelInput && formik.errors.applicationDetails?.applicationOwnerLabelInput?.label
                }
              >
                <Select
                  placeholder="Choose team"
                  selectedOption={labelInputToSelectDropdown(formik.values.applicationDetails?.applicationOwnerLabelInput)}
                  onChange={({ detail }) => {
                    formik.setFieldValue(
                      'applicationDetails.applicationOwnerLabelInput',
                      selectDropdownToLabel(detail.selectedOption, 'ApplicationOwner')
                    );
                  }}
                  onBlur={() => {
                    formik.setFieldTouched('applicationDetails.applicationOwner', true);
                  }}
                  options={labelInputToLabelDropdownOptions(appContext.listApplicationDropdowns?.listApplicationOwners)}
                  selectedAriaLabel="Selected"
                />
              </FormField>

              <FormField
                label="Module"
                description="Select the category that best describes this app"
                // @ts-ignore
                errorText={formik.touched.applicationDetails?.glAccounts && formik.errors.applicationDetails?.glAccounts?.label}
              >
                <Select
                  placeholder="Choose category"
                  selectedOption={labelInputToSelectDropdown(formik.values.applicationDetails?.glAccounts)}
                  onChange={({ detail }) => {
                    formik.setFieldValue('applicationDetails.glAccounts', selectDropdownToLabel(detail.selectedOption, 'GLAccount'));
                  }}
                  onBlur={() => {
                    formik.setFieldTouched('applicationDetails.glAccounts', true);
                  }}
                  options={labelInputToLabelDropdownOptions(appContext.listApplicationDropdowns?.listGLAccounts)}
                  filteringType="auto"
                  selectedAriaLabel="Selected"
                />
              </FormField>

              <FormField
                label="Product UI"
                description="Select the category that best describes this app"
                // @ts-ignore
                errorText={formik.touched.applicationDetails?.toolUI && formik.errors.applicationDetails?.toolUI?.label}
              >
                <Select
                  placeholder="Choose product UI"
                  selectedOption={labelInputToSelectDropdown(formik.values.applicationDetails?.toolUI)}
                  onChange={({ detail }) => {
                    formik.setFieldValue('applicationDetails.toolUI', selectDropdownToLabel(detail.selectedOption, 'ToolUI'));
                  }}
                  onBlur={() => {
                    formik.setFieldTouched('applicationDetails.toolUI', true);
                  }}
                  options={labelInputToLabelDropdownOptions(appContext.listApplicationDropdowns?.listToolUI)}
                  filteringType="auto"
                  selectedAriaLabel="Selected"
                />
              </FormField>
            </ColumnLayout>

            <ColumnLayout columns={2}>
              <FormField
                label={
                  <>
                    Application URL<i> - optional</i>
                  </>
                }
                description="Enter the URL for the app’s production environment"
                constraintText="Include http:// or https:// in the URL"
                errorText={formik.touched.applicationDetails?.applicationLink && formik.errors.applicationDetails?.applicationLink}
                stretch={true}
              >
                <Input
                  placeholder="Paste production URL"
                  onChange={({ detail }) => {
                    formik.setFieldValue('applicationDetails.applicationLink', detail.value);
                  }}
                  onBlur={() => {
                    formik.setFieldTouched('applicationDetails.applicationLink', true);
                  }}
                  value={formik.values.applicationDetails?.applicationLink || ''}
                />
              </FormField>

              <FormField
                label={
                  <>
                    Intro Video URL<i> - optional</i>
                  </>
                }
                description="Link a broadcast video where users can get an intro to your app"
                errorText={formik.errors.applicationDetails?.applicationIntroductionVideoUrl}
              >
                <Input
                  placeholder={BROADCAST_URL_EXAMPLE}
                  onChange={({ detail }) => {
                    formik.setFieldValue('applicationDetails.applicationIntroductionVideoUrl', detail.value);
                  }}
                  onBlur={() => {
                    formik.setFieldTouched('applicationDetails.applicationIntroductionVideoUrl', true);
                  }}
                  value={formik.values.applicationDetails?.applicationIntroductionVideoUrl || ''}
                />
              </FormField>
            </ColumnLayout>

            <FormField
              label="Short Application Description"
              description={`Short descriptions will be displayed in the navigation bar and “${Titles.TITLE_ALL_APPS}” page`}
              errorText={
                formik.touched.applicationDetails?.shortApplicationDescription && formik.errors.applicationDetails?.shortApplicationDescription
              }
              constraintText={characterCountConstraintMessage(
                SHORT_DESCRIPTION_MAX_LENGTH,
                formik.values.applicationDetails?.shortApplicationDescription?.length || 0
              )}
              stretch={true}
            >
              <Textarea
                placeholder="Enter the short description"
                onChange={({ detail }) => {
                  formik.setFieldValue('applicationDetails.shortApplicationDescription', detail.value);
                }}
                onBlur={() => {
                  formik.setFieldTouched('applicationDetails.shortApplicationDescription', true);
                }}
                value={formik.values.applicationDetails?.shortApplicationDescription || ''}
              />
            </FormField>

            <FormField
              label="Full Application Description"
              description="Full descriptions will be displayed on the app’s landing page"
              errorText={
                formik.touched.applicationDetails?.fullApplicationDescription && formik.errors.applicationDetails?.fullApplicationDescription
              }
              constraintText={characterCountConstraintMessage(
                FULL_DESCRIPTION_MAX_LENGTH,
                formik.values.applicationDetails?.fullApplicationDescription?.length || 0
              )}
              stretch={true}
            >
              <Textarea
                placeholder="Enter the full description"
                onChange={({ detail }) => {
                  formik.setFieldValue('applicationDetails.fullApplicationDescription', detail.value);
                }}
                onBlur={() => {
                  formik.setFieldTouched('applicationDetails.fullApplicationDescription', true);
                }}
                value={formik.values.applicationDetails?.fullApplicationDescription || ''}
              />
            </FormField>
          </SpaceBetween>
        </Container>
      </Box>

      {/* Application Logo */}
      <Box margin={{ bottom: 'l' }}>
        <Container
          header={
            <Header variant="h2" description="Choose a default logo or upload your own.">
              Application Logo
            </Header>
          }
        >
          <SpaceBetween size="m" direction="vertical">
            <ColumnLayout columns={2}>
              <SpaceBetween size="m" direction="vertical">
                <FormField label="App-Specific Upload" description="Toggle on to upload your own logo instead of using a default.">
                  <Toggle
                    checked={isAppSpecific}
                    onChange={({ detail }) => {
                      const newType: LogoType = detail.checked ? 'app-specific' : 'bundled';
                      setLogoFile([]);
                      setUploadError(null);

                      if (detail.checked) {
                        // Toggling ON: restore original S3 logo if available
                        const original = originalLogoConfigRef.current;
                        if (original?.s3Location && original?.uploadedFileName) {
                          formik.setFieldValue('applicationDetails.logoConfig', {
                            logoType: 'app-specific',
                            selectedLogoName: null,
                            uploadedFileName: original.uploadedFileName,
                            s3Location: original.s3Location
                          });
                          // Re-fetch preview from S3 (the effect will handle it since previewUrl is null)
                          setPreviewUrl(null);
                        } else {
                          formik.setFieldValue('applicationDetails.logoConfig', {
                            logoType: 'app-specific',
                            selectedLogoName: null,
                            uploadedFileName: null,
                            s3Location: null
                          });
                        }
                      } else {
                        // Toggling OFF: clear to bundled
                        setPreviewUrl(null);
                        formik.setFieldValue('applicationDetails.logoConfig', {
                          logoType: newType,
                          selectedLogoName: null,
                          uploadedFileName: null,
                          s3Location: null
                        });
                      }
                    }}
                  >
                    Use App-Specific Logo
                  </Toggle>
                </FormField>

                {!isAppSpecific ? (
                  <FormField
                    label="Default Logo"
                    description="Pick a pre-built logo from the library to represent your application."
                    constraintText="This logo appears in the navigation bar and app header."
                    errorText={
                      (formik.touched.applicationDetails as any)?.logoConfig?.selectedLogoName &&
                      (formik.errors.applicationDetails as any)?.logoConfig?.selectedLogoName
                    }
                  >
                    <Select
                      selectedOption={
                        formik.values.applicationDetails?.logoConfig?.selectedLogoName
                          ? {
                              value: formik.values.applicationDetails.logoConfig.selectedLogoName,
                              label: formik.values.applicationDetails.logoConfig.selectedLogoName.replace(/^(cloudscape|fa):/, '')
                            }
                          : null
                      }
                      onChange={({ detail }) => {
                        formik.setFieldValue('applicationDetails.logoConfig.selectedLogoName', detail.selectedOption.value);
                        formik.setFieldValue('applicationDetails.logoConfig.logoType', 'bundled');
                      }}
                      options={BUNDLED_LOGO_OPTIONS}
                      filteringType="auto"
                      placeholder="Search logos..."
                      empty="No matching logos found"
                    />
                  </FormField>
                ) : (
                  <FormField
                    label="Upload Logo"
                    description="Upload your own logo for this application."
                    constraintText="Accepted formats: SVG, PNG. Max size: 512 KB."
                    errorText={
                      uploadError ||
                      ((formik.touched.applicationDetails as any)?.logoConfig?.uploadedFileName &&
                        ((formik.errors.applicationDetails as any)?.logoConfig?.uploadedFileName ||
                          (formik.errors.applicationDetails as any)?.logoConfig?.s3Location))
                    }
                  >
                    <SpaceBetween size="xs" direction="vertical">
                      <FileUpload
                        value={logoFile}
                        onChange={async ({ detail }) => {
                          const file = detail.value[0];
                          setUploadError(null);

                          if (file) {
                            const validationError = AppLogoService.validateFile(file);
                            if (validationError) {
                              setUploadError(validationError);
                              return;
                            }

                            setLogoFile(detail.value);
                            setIsUploading(true);

                            const result = await AppLogoService.uploadToTemp(file, sessionIdRef.current);
                            setIsUploading(false);

                            if (result.success && result.tempS3Location) {
                              formik.setFieldValue('applicationDetails.logoConfig.uploadedFileName', file.name);
                              formik.setFieldValue('applicationDetails.logoConfig.s3Location', result.tempS3Location);
                              if (previewUrl) URL.revokeObjectURL(previewUrl);
                              setPreviewUrl(URL.createObjectURL(file));
                            } else {
                              setUploadError(result.error || 'Upload failed.');
                              setLogoFile([]);
                            }
                          } else {
                            setLogoFile([]);
                            setPreviewUrl(null);
                            formik.setFieldValue('applicationDetails.logoConfig.uploadedFileName', null);
                            formik.setFieldValue('applicationDetails.logoConfig.s3Location', null);
                          }
                        }}
                        accept=".svg,.png"
                        i18nStrings={{
                          uploadButtonText: () => 'Choose File',
                          dropzoneText: () => 'Drop file to upload',
                          removeFileAriaLabel: () => 'Remove file',
                          errorIconAriaLabel: 'Error'
                        }}
                        showFileSize
                      />
                      {isUploading && <StatusIndicator type="loading">Uploading logo...</StatusIndicator>}
                      {isDeleting && <StatusIndicator type="loading">Removing logo...</StatusIndicator>}
                      {!logoFile.length && formik.values.applicationDetails?.logoConfig?.uploadedFileName && (
                        <SpaceBetween size="xs" direction="horizontal" alignItems="center">
                          <StatusIndicator type="success">{formik.values.applicationDetails.logoConfig.uploadedFileName}</StatusIndicator>
                          <Button
                            variant="inline-link"
                            formAction="none"
                            onClick={async (event) => {
                              event.preventDefault();
                              const s3Location = formik.values.applicationDetails?.logoConfig?.s3Location;
                              if (s3Location) {
                                setIsDeleting(true);
                                await AppLogoService.deleteLogo(s3Location);
                                setIsDeleting(false);
                              }
                              originalLogoConfigRef.current = null;
                              setPreviewUrl(null);
                              formik.setFieldValue('applicationDetails.logoConfig.uploadedFileName', null);
                              formik.setFieldValue('applicationDetails.logoConfig.s3Location', null);
                            }}
                          >
                            Remove Logo
                          </Button>
                        </SpaceBetween>
                      )}
                    </SpaceBetween>
                  </FormField>
                )}
              </SpaceBetween>
            </ColumnLayout>

            {formik.values.applicationDetails?.logoConfig?.selectedLogoName || formik.values.applicationDetails?.logoConfig?.uploadedFileName ? (
              <FormField label="Preview">
                <EmbeddedNavPreview
                  selectedLogoName={formik.values.applicationDetails?.logoConfig?.selectedLogoName || null}
                  previewUrl={previewUrl}
                  displayName={formik.values.applicationDetails?.applicationName || 'App Name'}
                />
              </FormField>
            ) : null}
          </SpaceBetween>
        </Container>
      </Box>

      {/* Q Chat Configuration */}
      <Box margin={{ bottom: 'l' }}>
        <Container
          header={
            <Header variant="h2" description="Configure the QuickSight Q chat agent for this application.">
              Q Chat Configuration
            </Header>
          }
        >
          <SpaceBetween size="m" direction="vertical">
            <FormField label="Enable Q Chat" description="Allow users to interact with a QuickSight Q agent within this application.">
              <Toggle
                checked={formik.values.applicationDetails?.qChatConfig?.isEnabled || false}
                onChange={({ detail }) => {
                  formik.setFieldValue('applicationDetails.qChatConfig.isEnabled', detail.checked);
                  if (!detail.checked) {
                    formik.setFieldValue('applicationDetails.qChatConfig.agentUrl', null);
                  }
                }}
              >
                Enable Q Chat Agent
              </Toggle>
            </FormField>

            {formik.values.applicationDetails?.qChatConfig?.isEnabled && (
              <FormField
                label="QuickSight Agent URL"
                description="Paste the QuickSight embed URL for this application's Q agent"
                constraintText="Include the full https:// QuickSight embed URL"
                errorText={
                  (formik.touched.applicationDetails as any)?.qChatConfig?.agentUrl &&
                  (formik.errors.applicationDetails as any)?.qChatConfig?.agentUrl
                }
              >
                <Input
                  placeholder="https://us-east-1.quicksight.aws.amazon.com/sn/account/..."
                  onChange={({ detail }) => {
                    formik.setFieldValue('applicationDetails.qChatConfig.agentUrl', detail.value);
                  }}
                  onBlur={() => {
                    formik.setFieldTouched('applicationDetails.qChatConfig.agentUrl', true);
                  }}
                  value={formik.values.applicationDetails?.qChatConfig?.agentUrl || ''}
                />
              </FormField>
            )}
          </SpaceBetween>
        </Container>
      </Box>
    </>
  );
};

export default ApplicationDetails;
